import { useUserDetails } from '@commander/user-details';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import type { StepProps } from '../../types/onboarding.types';
import { DOMAIN_REGEX } from '../../utils/constants';
import { validateCompanyStep } from '../../utils/validators';
import FormInput from '../shared/FormInput';

const CompanyInfoStep: React.FC<StepProps> = ({
  data,
  onChange,
  validation,
  onNext,
  onBack: _onBack,
  isLoading,
  error,
  selectedRole: _selectedRole,
}) => {
  const { userDetails } = useUserDetails();
  const [companyName, setCompanyName] = useState(data.company || '');
  const [domain, setDomain] = useState(data.domain || '');
  const [localError, setLocalError] = useState<string | null>(null);
  const [domainHint, setDomainHint] = useState<string | null>(null);
  const hasUserEditedCompany = useRef(false);
  const hasUserEditedDomain = useRef(false);
  const domainHintTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear pending timeout on unmount to avoid setting state after unmount
  useEffect(() => {
    return () => {
      if (domainHintTimeout.current) {
        clearTimeout(domainHintTimeout.current);
        domainHintTimeout.current = null;
      }
    };
  }, []);

  // Prefill data from user details when available
  useEffect(() => {
    // Use company name from company_employee.company if available, fallback to userDetails.company
    const prefilledName =
      userDetails?.company_employee?.company?.name || userDetails?.company?.name;
    // Use domain_name from userDetails.company
    const prefilledDomain = userDetails?.company_employee?.company?.domain_name;

    // Only prefill if fields are empty, data is available, and user hasn't manually edited
    if (!data.company && prefilledName && !hasUserEditedCompany.current) {
      setCompanyName(prefilledName);
      onChange({ company: prefilledName, domain: data?.domain || prefilledDomain || '' });
    }

    if (!data.domain && prefilledDomain && !hasUserEditedDomain.current) {
      setDomain(prefilledDomain);
      onChange({ company: data.company || prefilledName || '', domain: prefilledDomain });
    }
  }, [userDetails, data.company, data.domain, onChange]);

  // Update form data when component receives new props
  useEffect(() => {
    setCompanyName(data.company || '');
    setDomain(data.domain || '');
  }, [data.company, data.domain]);

  const handleCompanyNameChange = (value: string) => {
    hasUserEditedCompany.current = true;
    setCompanyName(value);
    onChange({ company: value, domain });
    setLocalError(null);
  };

  const handleDomainChange = (value: string) => {
    hasUserEditedDomain.current = true;
    setDomain(value);
    onChange({ company: companyName, domain: value });
    setLocalError(null);

    if (domainHintTimeout.current) clearTimeout(domainHintTimeout.current);
    if (!value.trim() || DOMAIN_REGEX.test(value.trim())) {
      setDomainHint(null);
    } else {
      domainHintTimeout.current = setTimeout(() => {
        setDomainHint('Enter a valid URL (e.g., example.com or https://example.com)');
      }, 800);
    }
  };

  const handleSubmit = async () => {
    const formData = { ...data, company: companyName, domain };
    const validationData = await validation(formData);

    if (validationData) {
      setLocalError(null);
      onNext();
    } else {
      const result = validateCompanyStep(formData);
      setLocalError(result.error || 'Please check your input');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleNext = async () => {
    await handleSubmit();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        <FormInput
          label="Company Name"
          type="text"
          value={companyName}
          onChange={handleCompanyNameChange}
          className="flex"
          placeholder="Enter your company name"
          required
          helperText="This is how your company will appear throughout Commander"
          error={localError?.includes('company') ? localError : undefined}
        />

        <FormInput
          label="Website URL"
          type="url"
          value={domain}
          onChange={handleDomainChange}
          className="flex"
          placeholder="https://example.com"
          required
          helperText="We'll use this to personalize your outreach templates"
          hint={domainHint || undefined}
          error={localError?.includes('domain') ? localError : undefined}
        />
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Hidden navigation for parent component to handle */}
      <div className="hidden">
        <button
          type="button"
          onClick={handleNext}
          onKeyDown={handleKeyPress}
          disabled={isLoading}
        />
      </div>
    </div>
  );
};

export default CompanyInfoStep;
